import * as React from 'react';

import { graphql, PageProps } from 'gatsby';

import {
  Grid2 as Grid,
  Link,
  linkClasses,
  Skeleton,
  styled,
} from '@mui/material';

import { Language } from '@mewa/types';
import { ResponsiveGrid } from '@mewa/ui';

import t from '../../../libs/translate';
import {
  NavigationData,
  SearchPageTranslations,
} from '../../components/header/types';
import { SearchResult as SearchResultType } from '../../components/search/types';
import { useSearchResult } from '../../components/search/use-search-result';
import { GlobalPageContext } from '../../types';
import { translateSectionLinkNumerus } from './helper';
import SearchNoResultsSlider from './search-no-results-slider';
import { ServiceSegmentImages } from './search-result-categories';
import { SearchResultPageHeadline } from './search-result-page-headline';
import SearchSection, { SectionType } from './search-section';

export { Head } from '../../layouts/head';

export const query = graphql`
  query ($lang: String!, $locale: String!) {
    ...NavigationFragment
  }
`;

type Props = NavigationData;

type PageContext = GlobalPageContext & {
  translationFilter: string;
};

type SearchSectionData = {
  sectionType: SectionType;
  title: string;
  results: SearchResultType[];
  serviceSegmentImages?: ServiceSegmentImages;
};

const scrollToSection = (sectionId: string) => {
  document.getElementById(sectionId)?.scrollIntoView({ behavior: 'smooth' });
};

const SearchResultPage = ({
  data: {
    navigationClothing: { nodes: clothingServiceSegments },
    navigationNonClothing: { nodes: nonClothingServiceSegments },
    navigationLinks,
    searchPageTranslations,
    headerAndFooterTranslations,
  },
  pageContext,
  location,
}: PageProps<Props, PageContext>) => {
  const searchTermQuery = new URLSearchParams(location.search).get('q') ?? '';

  const { results, searchTerm, isLoading, totalCount } =
    useSearchResult(searchTermQuery);

  const serviceSegmentImages = Object.fromEntries([
    ...clothingServiceSegments.map((s) => [
      s.key,
      { image: s.assets.default, hoverImage: s.assets.hover },
    ]),
    ...nonClothingServiceSegments.map((s) => [
      s.key,
      { image: s.assets.default, hoverImage: s.assets.hover },
    ]),
  ]) as ServiceSegmentImages;

  const productText = translateSectionLinkNumerus(
    results.productDetail.length,
    'search_page_product_link',
    'search_page_products_link',
    searchPageTranslations,
  );

  const categoryText = translateSectionLinkNumerus(
    results.category.length,
    'search_page_category_link',
    'search_page_categories_link',
    searchPageTranslations,
  );

  const brandTopicsText = translateSectionLinkNumerus(
    results.brandTopic.length,
    'search_page_topic_link',
    'search_page_topics_link',
    searchPageTranslations,
  );

  const pressReleasesText = translateSectionLinkNumerus(
    results.pressRelease.length,
    'search_page_article_link',
    'search_page_articles_link',
    searchPageTranslations,
  );

  const blogArticlesText = translateSectionLinkNumerus(
    results.blogArticle.length,
    'search_page_blog_article_link',
    'search_page_blog_articles_link',
    searchPageTranslations,
  );

  const searchSectionLinks = () => {
    const result: React.ReactNode[] = [];

    if (results.productDetail.length > 0) {
      result.push(
        <SearchSectionLink
          key={SectionType.products}
          section={SectionType.products}
          text={productText}
        ></SearchSectionLink>,
      );
    }

    if (results.category.length > 0) {
      result.push(
        <SearchSectionLink
          key={SectionType.categories}
          section={SectionType.categories}
          text={categoryText}
        ></SearchSectionLink>,
      );
    }

    if (results.brandTopic.length) {
      result.push(
        <SearchSectionLink
          key={SectionType.brandTopics}
          section={SectionType.brandTopics}
          text={brandTopicsText}
        ></SearchSectionLink>,
      );
    }

    if (results.pressRelease.length > 0) {
      result.push(
        <SearchSectionLink
          key={SectionType.pressReleases}
          section={SectionType.pressReleases}
          text={pressReleasesText}
        ></SearchSectionLink>,
      );
    }

    if (results.blogArticle.length > 0) {
      result.push(
        <SearchSectionLink
          key={SectionType.blogArticles}
          section={SectionType.blogArticles}
          text={blogArticlesText}
        ></SearchSectionLink>,
      );
    }

    return result;
  };

  const searchSectionsData: SearchSectionData[] = [
    {
      sectionType: SectionType.products,
      title: t(searchPageTranslations, 'search_page_products_title', {
        amount: results.productDetail.length.toFixed(0),
      }),
      results: results.productDetail,
    },
    {
      sectionType: SectionType.categories,
      title: t(searchPageTranslations, 'search_page_categories_title', {
        amount: results.category.length.toFixed(0),
      }),
      results: results.category,
      serviceSegmentImages: serviceSegmentImages,
    },
    {
      sectionType: SectionType.brandTopics,
      title: t(searchPageTranslations, 'search_page_topics_title', {
        amount: results.brandTopic.length.toFixed(0),
      }),
      results: results.brandTopic,
    },
    {
      sectionType: SectionType.pressReleases,
      title: t(searchPageTranslations, 'search_page_articles_title', {
        amount: results.pressRelease.length.toFixed(0),
      }),
      results: results.pressRelease,
    },
    {
      sectionType: SectionType.blogArticles,
      title: t(searchPageTranslations, 'search_page_blog_articles_title', {
        amount: results.blogArticle.length.toFixed(0),
      }),
      results: results.blogArticle,
    },
  ];

  return isLoading ? (
    <LoadingState />
  ) : (
    <>
      <ResponsiveGrid>
        <Grid
          sx={{
            marginTop: ['54px', '174px'],
          }}
          size={12}
        >
          <SearchResultPageHeadline
            searchTerm={searchTerm}
            titleWithTotalCount={t(
              searchPageTranslations,
              totalCount !== 0
                ? 'search_page_results_title'
                : 'search_page_no_results_title',
              {
                amount: totalCount.toFixed(0),
              },
            )}
          />
          <SubTitle>{searchSectionLinks()}</SubTitle>
        </Grid>
      </ResponsiveGrid>

      {totalCount !== 0 ? (
        <SearchSections
          langPrefix={pageContext.langPrefix}
          data={searchSectionsData}
          translations={searchPageTranslations}
          blogSlug={navigationLinks.blog.generalSlug.slug}
          lang={pageContext.lang}
          locale={pageContext.locale}
        />
      ) : (
        <SearchNoResultsSlider
          langPrefix={pageContext.langPrefix}
          data={{
            clothingServiceSegments: clothingServiceSegments,
            nonClothingServiceSegments: nonClothingServiceSegments,
            navigationLinks,
            headerAndFooterTranslations,
          }}
          translations={searchPageTranslations}
        />
      )}
    </>
  );
};

const SearchSections = ({
  data,
  langPrefix,
  translations,
  lang,
  locale,
  blogSlug,
}: {
  data: SearchSectionData[];
  langPrefix: string;
  translations: SearchPageTranslations;
  lang: Language;
  locale: string;
  blogSlug: string;
}) => {
  return data.map(
    (section, i) =>
      section.results.length > 0 && (
        <SearchSection
          key={i}
          sectionType={section.sectionType}
          title={section.title}
          searchResults={section.results}
          langPrefix={langPrefix}
          serviceSegmentImages={section.serviceSegmentImages}
          translations={translations}
          detailsPageLabel={t(translations, 'show_product_details_label')}
          lang={lang}
          locale={locale}
          blogSlug={blogSlug}
        />
      ),
  );
};

const LoadingState = () => (
  <ResponsiveGrid>
    <Grid
      sx={{
        marginTop: ['50px', '174px'],
      }}
      size={12}
    >
      <StyledSkeleton variant="rectangular" />
      <StyledSkeleton
        variant="text"
        height={'50px'}
        sx={{ marginTop: '20px' }}
      />
      <SectionSkeleton />
      <SectionSkeleton />
      <SectionSkeleton />
    </Grid>
  </ResponsiveGrid>
);

const StyledSkeleton = styled(Skeleton)(({ theme }) => ({
  height: '118px',
  width: '100%',
  [theme.breakpoints.up('mdSm')]: {
    width: '50%',
  },
}));

const SectionSkeleton = () => (
  <Skeleton
    variant="rectangular"
    sx={{ width: ['100%', '80%'], marginTop: '40px' }}
    height={'350px'}
  />
);

const SearchSectionLink = ({
  section,
  text,
}: {
  section: string;
  text: string;
}) => {
  return (
    <div>
      <Link
        color="secondary"
        underline="always"
        onClick={() => scrollToSection(section)}
      >
        {text}
      </Link>
    </div>
  );
};

const SubTitle = styled('div')(({ theme }) => ({
  marginTop: 0,
  marginBottom: 0,
  display: 'flex',
  flexDirection: 'column',
  gap: '4px',

  [theme.breakpoints.up('sm')]: {
    marginBottom: '50px',
    flexDirection: 'row',

    '& *:not(:last-of-type):after': {
      content: '", "',
    },
  },

  [`&, .${linkClasses.root}`]: {
    ...theme.typography.body,
  },
}));

export default SearchResultPage;
